import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import { savePublicUserDisplayName, saveProfilePicture, deleteAccount } from '../services/DataStore';
import { PublicUser } from '../models/PublicUser';
import 'react-image-crop/dist/ReactCrop.css';

const EditProfile = ({ onClose }) => {
  const { currentUser, publicUserData } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [didNameChange, setDidNameChange] = useState(false);
  const [didProfilePicChange, setDidProfilePicChange] = useState(false);
  const [showCrop, setShowCrop] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [tempAvatarUrl, setTempAvatarUrl] = useState(null);
  const [croppedImageData, setCroppedImageData] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const imgRef = useRef(null);
  const modalRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingCancelled, setProcessingCancelled] = useState(false);

  useEffect(() => {
    if (publicUserData?.displayname) {
      setDisplayName(publicUserData.displayname);
    }
  }, [publicUserData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside the crop modal or delete confirmation dialog
      if (showCrop || showDeleteConfirm) return;
      
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClose, showCrop, showDeleteConfirm]);

  const handleDisplayNameChange = (e) => {
    setDisplayName(e.target.value);
    setDidNameChange(true);
    setHasChanges(true);
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '');
        setShowCrop(true);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    console.log('Original image dimensions:', width, 'x', height);

    // Set up initial crop
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        1,
        width,
        height
      ),
      width,
      height
    );
    setCrop(crop);
  };

  const handleCropComplete = (crop) => {
    setCompletedCrop(crop);
  };

  const handleCropCancel = () => {
    setProcessingCancelled(true);
    setShowCrop(false);
    setImgSrc('');
    setCrop(undefined);
    setCompletedCrop(null);
  };

  const processImageWithSize = async (image) => {
    if (processingCancelled) return null;

    // Get the cropped image from ReactCrop
    const croppedImage = await getCroppedImg(image, completedCrop);
    
    // Create a new image from the cropped data URL
    const img = new Image();
    img.src = croppedImage;
    
    // Wait for the image to load
    await new Promise(resolve => {
      img.onload = resolve;
    });

    // Create final 100x100 canvas
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = 100;
    finalCanvas.height = 100;
    const finalCtx = finalCanvas.getContext('2d');

    // Scale the cropped image to 100x100
    finalCtx.drawImage(
      img,
      0,
      0,
      img.width,
      img.height,
      0,
      0,
      100,
      100
    );

    return new Promise((resolve) => {
      finalCanvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        console.log('PNG size:', blob.size, 'bytes');
        resolve(blob);
      }, 'image/png');
    });
  };

  // Helper function to get cropped image from ReactCrop
  const getCroppedImg = (image, crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    return canvas.toDataURL('image/png');
  };

  const handleCropDone = async () => {
    if (!completedCrop || !imgRef.current) return;

    setIsProcessing(true);
    setProcessingCancelled(false);

    try {
      const image = imgRef.current;
      const blob = await processImageWithSize(image);
      
      if (!blob) {
        alert('Error processing image. Please try again.');
        return;
      }

      // Create temporary URL for immediate preview
      const tempUrl = URL.createObjectURL(blob);
      setTempAvatarUrl(tempUrl);
      
      // Convert blob to array buffer for upload
      const arrayBuffer = await blob.arrayBuffer();
      const byteArray = new Uint8Array(arrayBuffer);
      setCroppedImageData(byteArray);
      setDidProfilePicChange(true);
      setHasChanges(true);
      setShowCrop(false);
      setImgSrc('');

      // Reset the file input
      const fileInput = document.getElementById('avatar-upload');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
      console.error('Error processing profile picture:', error);
      alert('Error processing image. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Clean up temp URL when component unmounts
  useEffect(() => {
    return () => {
      if (tempAvatarUrl) {
        URL.revokeObjectURL(tempAvatarUrl);
      }
    };
  }, [tempAvatarUrl]);

  const handleSave = async () => {
    if (!didNameChange && !didProfilePicChange) {
      onClose?.();
      return;
    }
    try {
      
      if (didProfilePicChange && croppedImageData) {
        await saveProfilePicture(croppedImageData);
      }
      if (didNameChange) {
        await savePublicUserDisplayName(displayName);
      }
      
      onClose?.();
    } catch (error) {
      console.error('Error saving profile changes:', error);
      // TODO: Show error message to user
    }
  };

  const handleDeleteAccount = () => {
    console.log('Delete account button clicked, showing confirmation dialog');
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    console.log('Delete confirmation dialog - handleConfirmDelete called');
    try {
      console.log('Attempting to delete account...');
      await deleteAccount();
      console.log('Account deleted successfully, closing dialogs');
      setShowDeleteConfirm(false);
      onClose?.();
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please try again later.');
      console.log('Error occurred, only closing confirmation dialog');
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl max-w-2xl w-full h-[66vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Edit Profile</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Avatar Section */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-48 h-48 rounded-full border-4 border-accent overflow-hidden mb-4">
              {tempAvatarUrl ? (
                <img 
                  src={tempAvatarUrl} 
                  alt="New profile picture"
                  className="w-full h-full object-cover"
                />
              ) : publicUserData?.avatarurl ? (
                <img 
                  src={publicUserData.avatarurl} 
                  alt={publicUserData.displayname}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span className="material-symbols-outlined text-gray-500 text-6xl">account_circle</span>
                </div>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={onSelectFile}
              className="hidden"
              id="avatar-upload"
            />
            <label
              htmlFor="avatar-upload"
              className="text-accent hover:text-accent-dark font-medium cursor-pointer"
            >
              Change Picture
            </label>
          </div>

          {/* Display Name Section */}
          <div className="mb-8">
            <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
              Display Name
            </label>
            <div className="relative">
              <input
                type="text"
                id="displayName"
                value={displayName}
                onChange={handleDisplayNameChange}
                maxLength={PublicUser.DISPLAY_NAME_MAX_LENGTH}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent"
                placeholder="Enter your display name"
              />
              <div className="absolute right-2 top-2 text-sm text-gray-500">
                {displayName.length}/{PublicUser.DISPLAY_NAME_MAX_LENGTH}
              </div>
            </div>
          </div>

          {/* Login Credentials Section */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-700">Login Credentials</h3>
              <button
                onClick={handleDeleteAccount}
                className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                title="Permanently delete DiDeMa account"
              >
                <span className="material-symbols-outlined">delete</span>
              </button>
            </div>
            <div className="bg-gray-50 rounded-md p-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="material-symbols-outlined text-gray-600">account_circle</span>
                <span className="text-sm text-gray-600">
                  {currentUser?.email || 'No email available'}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                Your DiDeMa account is linked to your Google account. You can manage your account settings through Google.
              </p>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            className={`w-full px-4 py-2 rounded-md text-white font-medium transition-colors ${
              hasChanges 
                ? 'bg-accent hover:bg-accent-dark' 
                : 'bg-gray-400 cursor-not-allowed'
            }`}
            disabled={!hasChanges}
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* Image Crop Modal */}
      {showCrop && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] overflow-y-auto p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8">
            <h3 className="text-lg font-semibold mb-4">Crop Profile Picture</h3>
            <div className="mb-4 max-h-[calc(100vh-200px)] overflow-y-auto">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={handleCropComplete}
                aspect={1}
                circularCrop
              >
                <img
                  ref={imgRef}
                  src={imgSrc}
                  onLoad={onImageLoad}
                  alt="Crop preview"
                  className="max-h-[calc(100vh-300px)] mx-auto"
                />
              </ReactCrop>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={handleCropCancel}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                onClick={handleCropDone}
                className="px-4 py-2 bg-accent text-white rounded hover:bg-accent-dark flex items-center"
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Processing...
                  </>
                ) : (
                  'Done'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Account</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete your account? This action is permanent and cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProfile; 